// @ts-nocheck
/*
 * Port of disposition-forms/create.js (669 lines). BYTE-IDENTICAL.
 * Two independent ACL sub-options gate two extra form controls:
 *   - hasDispositionCustomTimingAcl â†’ "Custom wrap-up duration" input
 *   - hasDispositionAllowFollowUpsAcl â†’ "Enable follow-ups" switch
 * `normalizeField` is the payload builder â€” coerces String/Integer/Float/
 * Date/Boolean/Multi Select fields to their canonical shapes (numbers,
 * ISO date strings, arrays for options) with enable_* gating each
 * default/min/max. String has no min/max; Boolean & Multi Select never
 * have min/max regardless of enable_*.
 * Field-name allowed characters: `[a-zA-Z0-9 _\-?.(),'/:&]`. Wrap-up
 * duration must be 1â€“60s or null. Multi Select default must equal one of
 * the comma-separated options.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Tooltip: any = ({ children, id: _id, ...props }: any) => <span {...props}>{children}</span>;
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall } from '@/service/httpCall';
import { API_DISPOSITION_CREATE } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, ROUTES, USER_ROLES } from '@/utilities/constant';

const DISPOSITION_WRITE_ROLES = [USER_ROLES.SUPERADMIN, USER_ROLES.CALL_CENTRE_ADMIN];
import {
  DISPOSITION_CALL_DIRECTION_OPTIONS,
  isValidDispositionCallDirection,
} from '@/utilities/dispositionCallDirection';
import {
  hasDispositionCustomTimingAcl,
  hasDispositionAllowFollowUpsAcl,
} from '@/utilities/dispositionFormsAcl';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Plus, Save, Loader2, Trash2, Info } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const NAME_REGEX = /^[a-zA-Z0-9 _-]+$/;
const FIELD_NAME_REGEX = /^[a-zA-Z0-9 _\-?.(),'/:&]+$/;

const FIELD_TYPES = ['String', 'Textarea', 'Integer', 'Float', 'Date', 'Boolean', 'Multi Select'];

const NO_MIN_MAX_TYPES = ['String', 'Textarea', 'Boolean', 'Multi Select'];

// User-friendly labels for field types (display only; underlying values unchanged)
const TYPE_LABELS: any = {
  String: 'Text',
  Textarea: 'Text Area',
  Integer: 'Numbers',
  Float: 'Decimal Values',
  Boolean: 'Checkbox',
};

// Example values shown in the info-icon tooltip for each field type
const TYPE_EXAMPLES: any = {
  String: 'Any text. e.g. "Rohan", "Follow up needed"',
  Textarea: 'Long, multi-line text. e.g. detailed call notes',
  Integer: 'Whole numbers only. e.g. 5, 100, 250',
  Float: 'Numbers with decimals. e.g. 12.5, 99.99',
  Date: 'A calendar date. e.g. 2026-06-10',
  Boolean: 'A Yes/No checkbox. Checked = Yes, unchecked = No',
  'Multi Select': 'Pick one or more options. e.g. Hot, Warm, Cold',
};

const emptyField = () => ({
  field_name: '',
  field_type: 'String',
  is_required: false,
  attach_to_cdr: false,
  enable_default: false,
  enable_min: false,
  enable_max: false,
  field_default: '',
  field_min: '',
  field_max: '',
  options: '',
});

const normalizeField = (field: any) => {
  const fieldType = field?.field_type || 'String';

  const toNullableNumber = (val: any, { integerOnly }: any = {}) => {
    if (val === null || val === undefined || val === '') return null;
    const num = Number(val);
    if (!Number.isFinite(num)) return null;
    if (integerOnly && !Number.isInteger(num)) return null;
    return num;
  };

  const toNullableDateString = (val: any) => {
    if (!val) return null;
    const date = new Date(val);
    if (Number.isNaN(date.getTime())) return null;
    return String(val);
  };

  const toNullableDefault = () => {
    if (!field?.enable_default) return null;
    if (fieldType === 'Integer')
      return toNullableNumber(field?.field_default, { integerOnly: true });
    if (fieldType === 'Float') return toNullableNumber(field?.field_default);
    if (fieldType === 'Date') return toNullableDateString(field?.field_default);
    return String(field?.field_default ?? '').trim() || null;
  };

  const toNullableMin = () => {
    if (NO_MIN_MAX_TYPES.includes(fieldType)) return null;
    if (!field?.enable_min) return null;
    if (fieldType === 'Integer')
      return toNullableNumber(field?.field_min, { integerOnly: true });
    if (fieldType === 'Float') return toNullableNumber(field?.field_min);
    if (fieldType === 'Date') return toNullableDateString(field?.field_min);
    return toNullableNumber(field?.field_min, { integerOnly: true });
  };

  const toNullableMax = () => {
    if (NO_MIN_MAX_TYPES.includes(fieldType)) return null;
    if (!field?.enable_max) return null;
    if (fieldType === 'Integer')
      return toNullableNumber(field?.field_max, { integerOnly: true });
    if (fieldType === 'Float') return toNullableNumber(field?.field_max);
    if (fieldType === 'Date') return toNullableDateString(field?.field_max);
    return toNullableNumber(field?.field_max, { integerOnly: true });
  };

  return {
    field_name: String(field?.field_name || '').trim(),
    field_type: fieldType,
    is_required: Boolean(field?.is_required),
    attach_to_cdr: Boolean(field?.attach_to_cdr),
    field_default:
      fieldType === 'Boolean'
        ? Boolean(field?.field_default)
        : fieldType === 'Multi Select'
        ? field?.field_default || null
        : toNullableDefault(),
    field_min:
      fieldType === 'Boolean' || fieldType === 'Multi Select' ? null : toNullableMin(),
    field_max:
      fieldType === 'Boolean' || fieldType === 'Multi Select' ? null : toNullableMax(),
    options:
      fieldType === 'Multi Select'
        ? field.options
            .split(',')
            .map((x: string) => x.trim())
            .filter(Boolean)
        : null,
  };
};

const DispositionFormsCreate = () => {
  const navigate = useNavigate();
  const [buttonDisable, setButtonDisable] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});

  const [name, setName] = useState('');
  const [callDirection, setCallDirection] = useState('inbound');
  const [fields, setFields] = useState<any[]>([emptyField()]);
  const [wrapUpSeconds, setWrapUpSeconds] = useState('');
  const [followUpsEnabled, setFollowUpsEnabled] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (!user) return;
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
    setUserLoaded(true);
  }, []);

  const canSetCustomTiming = useMemo(
    () => hasDispositionCustomTimingAcl(loggedInUserACL, { isSuperAdmin }),
    [loggedInUserACL, isSuperAdmin],
  );

  const canSetFollowUps = useMemo(
    () => hasDispositionAllowFollowUpsAcl(loggedInUserACL, { isSuperAdmin }),
    [loggedInUserACL, isSuperAdmin],
  );

  const canAdd = useMemo(
    () =>
      DISPOSITION_WRITE_ROLES.includes(getUser()?.role) &&
      !!loggedInUserACL?.[MODULE_ACL.DISPOSITION_FORMS]?.add,
    [loggedInUserACL],
  );

  useEffect(() => {
    if (!userLoaded) return;
    if (!canAdd) {
      toast.error("You don't have permission to create disposition forms");
      navigate(`/${ROUTES.DISPOSITION_FORMS.LIST}`);
    }
  }, [canAdd, navigate, userLoaded]);

  const updateField = (index: number, key: string, value: any) => {
    setFields((prev) => {
      const next = [...prev];
      const existing = next[index] || emptyField();
      const updated: any = { ...existing, [key]: value };
      if (key === 'field_type') {
        updated.enable_default = false;
        updated.enable_min = false;
        updated.enable_max = false;
        updated.field_default = '';
        updated.field_min = '';
        updated.field_max = '';
        updated.options = '';
      }
      next[index] = updated;
      return next;
    });
  };

  const addField = () => setFields((prev) => [...prev, emptyField()]);

  const removeField = (index: number) =>
    setFields((prev) => prev.filter((_, idx) => idx !== index));

  const getValueMeta = (fieldType: string) => {
    if (fieldType === 'Integer')
      return { inputType: 'number', step: 1, placeholder: 'Numbers' };
    if (fieldType === 'Float')
      return { inputType: 'number', step: 'any', placeholder: 'Decimal Values' };
    if (fieldType === 'Date')
      return { inputType: 'date', step: undefined, placeholder: '' };
    return { inputType: 'text', step: undefined, placeholder: 'Text' };
  };

  const getMinMaxMeta = (fieldType: string) => {
    if (fieldType === 'Integer')
      return { inputType: 'number', step: 1, placeholder: 'Numbers' };
    if (fieldType === 'Float')
      return { inputType: 'number', step: 'any', placeholder: 'Decimal Values' };
    if (fieldType === 'Date')
      return { inputType: 'date', step: undefined, placeholder: '' };
    return { inputType: 'number', step: 1, placeholder: 'Length' };
  };

  const validate = () => {
    if (!name.trim()) {
      toast.warning('Name is required');
      return false;
    }

    if (!isValidDispositionCallDirection(callDirection)) {
      toast.warning('Select call flow (Inbound, Outbound, or Both)');
      return false;
    }

    if (!Array.isArray(fields) || fields.length === 0) {
      toast.warning('At least one field is required');
      return false;
    }

    for (let i = 0; i < fields.length; i += 1) {
      const normalized = normalizeField(fields[i]);
      if (!normalized.field_name) {
        toast.warning(`Field name is required (row ${i + 1})`);
        return false;
      }
      if (!FIELD_TYPES.includes(normalized.field_type)) {
        toast.warning(`Invalid field type (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_default && normalized.field_default === null) {
        toast.warning(`Invalid default value (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_min && normalized.field_min === null) {
        toast.warning(`Invalid min value (row ${i + 1})`);
        return false;
      }
      if (fields[i]?.enable_max && normalized.field_max === null) {
        toast.warning(`Invalid max value (row ${i + 1})`);
        return false;
      }
      if (!FIELD_NAME_REGEX.test(normalized.field_name)) {
        toast.warning(`Special characters not allowed in field name (row ${i + 1})`);
        return false;
      }

      if (
        normalized.field_type !== 'Date' &&
        typeof normalized.field_min === 'number' &&
        typeof normalized.field_max === 'number' &&
        normalized.field_min > normalized.field_max
      ) {
        toast.warning(`Min cannot be greater than max (row ${i + 1})`);
        return false;
      }

      if (
        normalized.field_type === 'Date' &&
        normalized.field_min &&
        normalized.field_max &&
        new Date(normalized.field_min).getTime() > new Date(normalized.field_max).getTime()
      ) {
        toast.warning(`Min date cannot be after max date (row ${i + 1})`);
        return false;
      }
      if (
        normalized.field_type === 'Multi Select' &&
        (!fields[i]?.options ||
          fields[i].options
            .split(',')
            .map((x: string) => x.trim())
            .filter(Boolean).length < 1)
      ) {
        toast.warning(`At least one option required (row ${i + 1})`);
        return false;
      }
      if (normalized.field_type === 'Multi Select') {
        const options = (fields[i]?.options || '')
          .split(',')
          .map((x: string) => x.trim())
          .filter(Boolean);

        if (options.length < 1) {
          toast.warning(`At least one option required (row ${i + 1})`);
          return false;
        }

        if (fields[i]?.field_default?.trim() && options.length === 0) {
          toast.warning(`Default option requires options (row ${i + 1})`);
          return false;
        }

        if (
          fields[i]?.field_default?.trim() &&
          !options.includes(fields[i].field_default.trim())
        ) {
          toast.warning(`Default must match one of the options (row ${i + 1})`);
          return false;
        }
      }
    }

    return true;
  };

  const submitForm = () => {
    if (!validate()) return;

    setButtonDisable(true);
    const payload: any = {
      name: name.trim(),
      call_direction: callDirection,
      fields: fields.map(normalizeField),
    };

    if (canSetCustomTiming) {
      const trimmed = String(wrapUpSeconds ?? '').trim();
      payload.wrap_up_seconds = trimmed === '' ? null : Number(trimmed);
      if (
        payload.wrap_up_seconds !== null &&
        (!Number.isInteger(payload.wrap_up_seconds) ||
          payload.wrap_up_seconds < 1 ||
          payload.wrap_up_seconds > 60)
      ) {
        toast.warning('Wrap-up duration must be between 1 and 60 seconds');
        setButtonDisable(false);
        return;
      }
    }

    if (canSetFollowUps) {
      payload.follow_ups_enabled = followUpsEnabled;
    }

    postApiCall(API_DISPOSITION_CREATE, payload)
      .then(() => {
        toast.success('Disposition form created successfully');
        setTimeout(() => navigate(`/${ROUTES.DISPOSITION_FORMS.LIST}`), 800);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Unable to create disposition form');
        setButtonDisable(false);
      });
  };

  if (!userLoaded || !canAdd) return null;

  return (
    <ScrollPage>
      <PageHeader
        title="Create Disposition Form"
        description="Compose a disposition form with call flow, fields, and wrap-up timings."
        actions={
          <Button asChild variant="outline">
            <RouterLink to={`/${ROUTES.DISPOSITION_FORMS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </RouterLink>
          </Button>
        }
      />
      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || NAME_REGEX.test(value)) {
                    setName(value);
                  }
                }}
                placeholder="Only letters, numbers, space, _ and - allowed"
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Call flow <span className="text-destructive">*</span>
              </label>
              <select
                value={callDirection}
                onChange={(e) => setCallDirection(e.target.value)}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              >
                {DISPOSITION_CALL_DIRECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Both makes this form available for inbound and outbound calls.
              </p>
            </div>

            {canSetCustomTiming ? (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">
                  Custom wrap-up duration (seconds)
                </label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={wrapUpSeconds}
                  onChange={(e) => setWrapUpSeconds(e.target.value)}
                  placeholder="Optional — leave empty for system default"
                  className="input-embossed h-10 w-full rounded-md px-3 text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Leave empty to use the system default wrap-up time. Enter seconds only if this form needs a custom duration.
                </p>
              </div>
            ) : null}

            {canSetFollowUps ? (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Follow-ups</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={followUpsEnabled}
                    onClick={() => setFollowUpsEnabled(!followUpsEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      followUpsEnabled ? 'bg-emerald-500' : 'bg-muted/70'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                        followUpsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {followUpsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  When enabled, agents can schedule a follow-up date and time while submitting this form.
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-3 rounded-md border border-primary/20 bg-primary/[0.04] p-2.5 text-[11px] text-muted-foreground">
            <strong className="text-foreground">Note:</strong> If a field named{' '}
            <strong className="text-foreground">"Contact Number"</strong> exists in this form, its value will be automatically filled using the call's{' '}
            <strong className="text-foreground">To/From</strong> number.
            <br />
            Also, if you add fields named{' '}
            <strong className="text-foreground">"First Name"</strong> and{' '}
            <strong className="text-foreground">"Last Name"</strong>, whatever the agent types
            there will be saved to your Contacts list against that phone number — so the same
            caller will already show their name next time.
          </div>

          <div className="mt-4 rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              <span>
                <strong className="text-foreground">Field types & example values:</strong> hover a tag to see what values it accepts.
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FIELD_TYPES.map((t) => (
                <span
                  key={t}
                  title={TYPE_EXAMPLES[t]}
                  className="inline-flex cursor-help items-center gap-1 rounded-full bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground ring-1 ring-inset ring-border/60"
                >
                  {TYPE_LABELS[t] || t}
                  <Info className="h-3 w-3 text-muted-foreground" />
                </span>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-border/50 pt-3">
            <h5 className="text-sm font-bold text-foreground">Fields</h5>
            <Button variant="outline" size="sm" onClick={addField} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add Field
            </Button>
          </div>

          {fields.map((field: any, idx: number) => {
            const fieldType = field?.field_type || 'String';
            const defaultMeta = getValueMeta(fieldType);
            const minMaxMeta = getMinMaxMeta(fieldType);
            return (
              <div
                key={idx}
                className="mt-3 rounded-xl border border-border/60 bg-muted/20 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                    Field #{idx + 1}
                  </span>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField(idx)}
                      title="Remove field"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                  <div className="space-y-1.5 md:col-span-4">
                    <label className="text-xs font-semibold text-foreground">
                      Field Name <span className="text-destructive">*</span>
                    </label>
                    <input
                      value={field.field_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || FIELD_NAME_REGEX.test(value)) {
                          updateField(idx, 'field_name', value);
                        }
                      }}
                      placeholder="e.g. Customer Name"
                      className="input-embossed h-9 w-full rounded-md px-2.5 text-sm"
                    />
                  </div>

                  <div className="space-y-1.5 md:col-span-3">
                    <label className="text-xs font-semibold text-foreground">Type</label>
                    <select
                      value={fieldType}
                      onChange={(e) => updateField(idx, 'field_type', e.target.value)}
                      className="input-embossed h-9 w-full rounded-md px-2.5 text-sm"
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {TYPE_LABELS[t] || t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-wrap items-end gap-4 md:col-span-5">
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-foreground">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border accent-primary"
                        checked={Boolean(field.is_required)}
                        onChange={(e) =>
                          updateField(idx, 'is_required', e.target.checked)
                        }
                      />
                      Required
                    </label>
                    <label
                      className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-foreground"
                      title="Include this field's submitted value in the CDR listing Disposition column"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border accent-primary"
                        checked={Boolean(field.attach_to_cdr)}
                        onChange={(e) =>
                          updateField(idx, 'attach_to_cdr', e.target.checked)
                        }
                      />
                      Attach to CDR
                    </label>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-foreground">Default</label>
                      {fieldType !== 'Boolean' && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border accent-primary"
                          checked={Boolean(field.enable_default)}
                          onChange={(e) =>
                            updateField(idx, 'enable_default', e.target.checked)
                          }
                        />
                      )}
                    </div>

                    {fieldType === 'Boolean' ? (
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border accent-primary"
                          checked={Boolean(field.field_default)}
                          onChange={(e) =>
                            updateField(idx, 'field_default', e.target.checked)
                          }
                        />
                        Default Checked
                      </label>
                    ) : fieldType === 'Textarea' ? (
                      <textarea
                        disabled={!field.enable_default}
                        rows={3}
                        value={field.field_default ?? ''}
                        onChange={(e) =>
                          updateField(idx, 'field_default', e.target.value)
                        }
                        placeholder="Default text"
                        className="input-embossed w-full rounded-md px-2.5 py-2 text-sm disabled:opacity-50"
                      />
                    ) : fieldType === 'Multi Select' ? (
                      <input
                        disabled={!field.enable_default}
                        value={field.field_default ?? ''}
                        onChange={(e) =>
                          updateField(idx, 'field_default', e.target.value)
                        }
                        placeholder="Default option"
                        className="input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50"
                      />
                    ) : (
                      <input
                        disabled={!field.enable_default}
                        type={defaultMeta.inputType}
                        step={defaultMeta.step}
                        value={field.field_default ?? ''}
                        onChange={(e) =>
                          updateField(idx, 'field_default', e.target.value)
                        }
                        placeholder={defaultMeta.placeholder}
                        className="input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50"
                      />
                    )}
                  </div>

                  {fieldType === 'Multi Select' && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-foreground">
                        Options <span className="text-destructive">*</span>
                      </label>
                      <input
                        value={field.options}
                        onChange={(e) => updateField(idx, 'options', e.target.value)}
                        placeholder="Option1, Option2, Option3"
                        className="input-embossed h-9 w-full rounded-md px-2.5 text-sm"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Separate options with commas
                      </p>
                    </div>
                  )}

                  {!NO_MIN_MAX_TYPES.includes(fieldType) && (
                    <>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-foreground">Min</label>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border accent-primary"
                            checked={Boolean(field.enable_min)}
                            onChange={(e) =>
                              updateField(idx, 'enable_min', e.target.checked)
                            }
                          />
                        </div>
                        <input
                          disabled={!field.enable_min}
                          type={minMaxMeta.inputType}
                          step={minMaxMeta.step}
                          value={field.field_min ?? ''}
                          onChange={(e) =>
                            updateField(idx, 'field_min', e.target.value)
                          }
                          placeholder={minMaxMeta.placeholder}
                          className="input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-foreground">Max</label>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border accent-primary"
                            checked={Boolean(field.enable_max)}
                            onChange={(e) =>
                              updateField(idx, 'enable_max', e.target.checked)
                            }
                          />
                        </div>
                        <input
                          disabled={!field.enable_max}
                          type={minMaxMeta.inputType}
                          step={minMaxMeta.step}
                          value={field.field_max ?? ''}
                          onChange={(e) =>
                            updateField(idx, 'field_max', e.target.value)
                          }
                          placeholder={minMaxMeta.placeholder}
                          className="input-embossed h-9 w-full rounded-md px-2.5 text-sm disabled:opacity-50"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <Button asChild variant="outline">
              <RouterLink to={`/${ROUTES.DISPOSITION_FORMS.LIST}`}>Cancel</RouterLink>
            </Button>
            <Button
              onClick={submitForm}
              type="button"
              disabled={buttonDisable}
              className="gap-1.5"
            >
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Creating…' : 'Create form'}
            </Button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

export default DispositionFormsCreate;
